// Vivasvan i18n — EN / FR / PT
// Human-crafted institutional-tone translations. Access through the useT() hook.

export const LOCALES = [
  { code: "en", label: "EN", name: "English" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "pt", label: "PT", name: "Português" },
];

const en = {
  nav: {
    home: "Home",
    products: "Products",
    logistics: "Logistical Network",
    compliance: "Fiduciary & Compliance",
    contact: "Contact",
    cta: "Initiate Trade",
  },
  hero: {
    eyebrow: "Est. 2026 · Pan-African Commodity Trading Desk",
    h1a: "Sovereign Energy.",
    h1b: "Institutional Scale.",
    h1c: "Pan-African Distribution.",
    sub: "Vivasvan Energies Ventures (Pty) Ltd is a premier physical commodity trader and logistics partner, executing high-volume energy and material flows across the African continent with absolute fiduciary precision.",
    cta_primary: "Review Product Catalog",
    cta_secondary: "Compliance & Licensing Portal",
    trust: [
      { k: "AML/KYC", v: "Zero Tolerance" },
      { k: "LBMA", v: "Certified Channels" },
      { k: "Bank-to-Bank", v: "Trade Finance" },
      { k: "Registered", v: "Depot Network" },
    ],
  },
  products: {
    eyebrow: "Product Catalog · 02",
    h2a: "Physical commodities.",
    h2b: "Engineered supply chains.",
    sub: "Two verticals. One institutional-grade trading desk. Every allocation is underwritten by verified refinery contracts, licensed depot storage, and bank-endorsed offtake instruments.",
    tab_energy: "Energy & Gas Solutions",
    tab_solid: "Solid Commodities & Minerals",
    active: "Active Allocation",
    energy: [
      { name: "Diesel (10ppm / 50ppm)", body: "High-grade industrial gas oil sourced from leading refineries for bulk transport fleets, heavy machinery, and industrial grids.", tag: "Bulk / Industrial" },
      { name: "Petrol (ULP 95 / 93)", body: "Clean-burning unleaded fuel meeting SANS and regional South African regulatory parameters.", tag: "Regulated Grade" },
      { name: "Liquefied Petroleum Gas (LPG)", body: "Industrial-scale, high-calorific bulk thermal gas solutions for metallurgical furnaces and heavy commercial distribution.", tag: "High Calorific" },
      { name: "Compressed Natural Gas (CNG)", body: "Highly sustainable alternative gaseous fuel formulas optimised for heavy commercial fleet transitions.", tag: "Fleet Transition" },
    ],
    solid: [
      { name: "Gold (Au Bullion)", body: "Elite physical gold trading pipelines, working strictly through London Bullion Market Association (LBMA) approved secure channels.", tag: "LBMA Channels" },
      { name: "Bauxite", body: "Raw, high-grade aluminium ore sourced directly from prime African deposits for major global export corridors.", tag: "Export Corridor" },
      { name: "Coal", body: "Graded high-CV thermal power coal engineered for power grids, industrial furnaces, and metallurgy.", tag: "High-CV Thermal" },
    ],
  },
  logistics: {
    eyebrow: "Logistical Powerhouse · 03",
    h2a: "A network built on",
    h2b: "strategic infrastructure.",
    sub: "Our operations do not rely on speculation. We move physical products safely through active off-take agreements and key working relationships with major bulk storage depots across Africa\u2019s critical logistics corridors.",
    node: "Node",
    active: "Active",
    depots: [
      { region: "South Africa", node: "Inland Distribution", body: "Strategic reserves and inland distribution pipelines servicing heavy industrial and agricultural sectors." },
      { region: "Namibia", node: "Walvis Bay", body: "Port integration driving fuel and bauxite supply chains into neighbouring landlocked Southern African nations." },
      { region: "Mozambique", node: "Beira · Maputo", body: "Fast-track off-take pipelines and secure deepwater terminal cargo releases." },
      { region: "Tanzania", node: "Dar es Salaam", body: "Direct marine import integration servicing high-demand East African corporate mineral pipelines." },
      { region: "Botswana", node: "Interior Corridor", body: "Consistent, high-volume energy deliveries supporting heavy-duty diamond mining infrastructures." },
      { region: "DRC", node: "Copper · Cobalt Belt", body: "Secure bulk fuel carrier solutions engineered specifically for copper and cobalt excavation plants." },
    ],
  },
  compliance: {
    eyebrow: "Compliance Manifesto · 04",
    h2a: "Fiduciary Compliance &",
    h2b: "Licensed Execution.",
    body: "Vivasvan Energies Ventures (Pty) Ltd operates under a strict, unyielding compliance mandate. Every physical transaction, trade finance structure, and logistical movement is executed exclusively through registered corporate entities, certified bank-to-bank transactions, and licensed financiers. We maintain zero-tolerance AML/KYC protocols and trade solely with verified, licensed storage depot owners, port operators, and accredited financial institutions. Security, legitimacy, and regulatory alignment are our highest priorities.",
    pillars: [
      { k: "AML / KYC", v: "Zero-Tolerance Protocols" },
      { k: "Bank-to-Bank", v: "Certified Trade Finance" },
      { k: "Licensed", v: "Depot & Port Operators" },
      { k: "Registered", v: "Corporate Entities Only" },
    ],
  },
  contact: {
    eyebrow: "Trade Conduit · 05",
    h2a: "Establish a",
    h2b: "Trade Conduit.",
    sub: "Connect with our global trading desk to negotiate allocations, logistics, or project financing structures. All fields must be verified to trigger compliance dispatch.",
    entity_label: "Registered Entity",
    entity_value: "Vivasvan Energies Ventures (Pty) Ltd",
    portal_label: "Official Portal",
    desk_label: "Trading Desk",
    field_company: "Company Legal Name",
    field_license: "Import-Export License / Corporate Registration No.",
    field_email: "Direct Procurement Email",
    field_product: "Product of Interest",
    field_volume: "Requested Monthly Volume / Capacity Required",
    placeholder_company: "e.g. Sovereign Holdings Ltd",
    placeholder_license: "e.g. IEC-2026-00891",
    placeholder_email: "procurement@yourcompany.com",
    placeholder_product: "Select allocation…",
    placeholder_volume: "e.g. 50,000 MT / month",
    submit: "Submit Trade Request",
    submit_pending: "Dispatching…",
    disclaimer: "Submissions are logged into our secure compliance ledger. Non-verifiable entities will be filtered before dispatch.",
    err_title: "All fields must be verified",
    err_desc: "Complete every field to trigger compliance dispatch.",
    ok_title: "Trade conduit request received",
    ok_desc: "Our trading desk will respond via a verified corporate channel.",
    fail_title: "Submission failed",
    fail_desc: "Please verify all fields.",
  },
  footer: {
    copyright: "© 2026 Vivasvan Energies Ventures (Pty) Ltd · Est. 2026 · All Rights Reserved",
    tagline: "Physical commodity trading executed exclusively via registered corporate entities, certified bank-to-bank transactions, and licensed financiers.",
    admin_link: "Compliance Ledger →",
  },
  lang: {
    switch_aria: "Switch language",
  },
};

const fr = {
  nav: {
    home: "Accueil",
    products: "Produits",
    logistics: "Réseau Logistique",
    compliance: "Fiduciaire & Conformité",
    contact: "Contact",
    cta: "Initier un Trade",
  },
  hero: {
    eyebrow: "Fondée 2026 · Trading de Matières Premières Pan-Africain",
    h1a: "Énergie Souveraine.",
    h1b: "Échelle Institutionnelle.",
    h1c: "Distribution Pan-Africaine.",
    sub: "Vivasvan Energies Ventures (Pty) Ltd est un trader et partenaire logistique de premier plan en matières premières physiques, orchestrant des flux d’énergie et de matériaux à grand volume sur le continent africain avec une précision fiduciaire absolue.",
    cta_primary: "Consulter le Catalogue",
    cta_secondary: "Portail de Conformité & Licences",
    trust: [
      { k: "AML/KYC", v: "Tolérance Zéro" },
      { k: "LBMA", v: "Canaux Certifiés" },
      { k: "Banque-à-Banque", v: "Financement Commercial" },
      { k: "Enregistré", v: "Réseau de Dépôts" },
    ],
  },
  products: {
    eyebrow: "Catalogue Produits · 02",
    h2a: "Matières premières physiques.",
    h2b: "Chaînes d’approvisionnement conçues.",
    sub: "Deux verticales. Un desk de trading de qualité institutionnelle. Chaque allocation est adossée à des contrats de raffinerie vérifiés, un stockage en dépôt agréé et des instruments d’off-take endossés par les banques.",
    tab_energy: "Solutions Énergie & Gaz",
    tab_solid: "Matières Premières Solides & Minéraux",
    active: "Allocation Active",
    energy: [
      { name: "Diesel (10ppm / 50ppm)", body: "Gasoil industriel haut de gamme provenant de raffineries de premier plan pour flottes de transport en vrac, machinerie lourde et réseaux industriels.", tag: "Vrac / Industriel" },
      { name: "Essence (ULP 95 / 93)", body: "Carburant sans plomb à combustion propre conforme aux paramètres réglementaires SANS et sud-africains régionaux.", tag: "Qualité Réglementée" },
      { name: "Gaz de Pétrole Liquéfié (GPL)", body: "Solutions thermiques en vrac à haut pouvoir calorifique, échelle industrielle, pour fours métallurgiques et distribution commerciale lourde.", tag: "Haut Pouvoir Calorifique" },
      { name: "Gaz Naturel Comprimé (GNC)", body: "Formules de carburants gazeux alternatifs très durables, optimisées pour la transition des flottes commerciales lourdes.", tag: "Transition Flotte" },
    ],
    solid: [
      { name: "Or (Au Bullion)", body: "Pipelines d’élite pour le négoce d’or physique, opérant strictement via les canaux sécurisés agréés par la London Bullion Market Association (LBMA).", tag: "Canaux LBMA" },
      { name: "Bauxite", body: "Minerai d’aluminium brut de haute qualité extrait directement de gisements africains prioritaires pour les grands corridors d’exportation mondiaux.", tag: "Corridor d’Export" },
      { name: "Charbon", body: "Charbon thermique haut PCI calibré, conçu pour les réseaux électriques, les fours industriels et la métallurgie.", tag: "Thermique Haut-PCI" },
    ],
  },
  logistics: {
    eyebrow: "Puissance Logistique · 03",
    h2a: "Un réseau bâti sur",
    h2b: "une infrastructure stratégique.",
    sub: "Nos opérations ne reposent pas sur la spéculation. Nous transportons des produits physiques en toute sécurité grâce à des accords d’off-take actifs et à des relations de travail clés avec les principaux dépôts de stockage sur les corridors logistiques critiques de l’Afrique.",
    node: "Nœud",
    active: "Actif",
    depots: [
      { region: "Afrique du Sud", node: "Distribution Intérieure", body: "Réserves stratégiques et pipelines de distribution intérieurs desservant les secteurs industriel lourd et agricole." },
      { region: "Namibie", node: "Walvis Bay", body: "Intégration portuaire animant les chaînes d’approvisionnement en carburant et bauxite vers les pays enclavés voisins d’Afrique australe." },
      { region: "Mozambique", node: "Beira · Maputo", body: "Pipelines d’off-take accélérés et libérations sécurisées de cargaisons en terminal deepwater." },
      { region: "Tanzanie", node: "Dar es Salaam", body: "Intégration directe des importations maritimes desservant les pipelines corporate miniers à forte demande d’Afrique de l’Est." },
      { region: "Botswana", node: "Corridor Intérieur", body: "Livraisons énergétiques constantes et à haut volume soutenant les infrastructures minières diamantifères lourdes." },
      { region: "RDC", node: "Ceinture Cuivre · Cobalt", body: "Solutions sécurisées de transport de carburant en vrac conçues spécifiquement pour les usines d’extraction de cuivre et cobalt." },
    ],
  },
  compliance: {
    eyebrow: "Manifeste de Conformité · 04",
    h2a: "Conformité Fiduciaire &",
    h2b: "Exécution sous Licence.",
    body: "Vivasvan Energies Ventures (Pty) Ltd opère sous un mandat de conformité strict et inflexible. Chaque transaction physique, structure de financement commercial et mouvement logistique est exécuté exclusivement via des entités corporate enregistrées, des transactions banque-à-banque certifiées et des financiers licenciés. Nous maintenons des protocoles AML/KYC à tolérance zéro et négocions uniquement avec des propriétaires de dépôts, opérateurs portuaires et institutions financières accrédités et licenciés. La sécurité, la légitimité et l’alignement réglementaire sont nos priorités absolues.",
    pillars: [
      { k: "AML / KYC", v: "Protocoles Tolérance-Zéro" },
      { k: "Banque-à-Banque", v: "Financement Certifié" },
      { k: "Sous Licence", v: "Dépôts & Opérateurs Portuaires" },
      { k: "Enregistré", v: "Entités Corporate Uniquement" },
    ],
  },
  contact: {
    eyebrow: "Conduit Commercial · 05",
    h2a: "Établir un",
    h2b: "Conduit Commercial.",
    sub: "Contactez notre desk de trading global pour négocier allocations, logistique ou structures de financement de projet. Tous les champs doivent être vérifiés pour déclencher la dépêche de conformité.",
    entity_label: "Entité Enregistrée",
    entity_value: "Vivasvan Energies Ventures (Pty) Ltd",
    portal_label: "Portail Officiel",
    desk_label: "Desk de Trading",
    field_company: "Raison Sociale de l’Entreprise",
    field_license: "Licence Import-Export / N° d’Enregistrement Corporate",
    field_email: "Email Direct de Procurement",
    field_product: "Produit d’Intérêt",
    field_volume: "Volume Mensuel Demandé / Capacité Requise",
    placeholder_company: "ex. Sovereign Holdings Ltd",
    placeholder_license: "ex. IEC-2026-00891",
    placeholder_email: "procurement@votreentreprise.com",
    placeholder_product: "Sélectionner l’allocation…",
    placeholder_volume: "ex. 50 000 MT / mois",
    submit: "Soumettre la Demande",
    submit_pending: "Dépêche en cours…",
    disclaimer: "Les soumissions sont consignées dans notre registre de conformité sécurisé. Les entités non vérifiables seront filtrées avant dépêche.",
    err_title: "Tous les champs doivent être vérifiés",
    err_desc: "Complétez chaque champ pour déclencher la dépêche.",
    ok_title: "Demande de conduit reçue",
    ok_desc: "Notre desk répondra via un canal corporate vérifié.",
    fail_title: "Échec de la soumission",
    fail_desc: "Veuillez vérifier tous les champs.",
  },
  footer: {
    copyright: "© 2026 Vivasvan Energies Ventures (Pty) Ltd · Fondée 2026 · Tous Droits Réservés",
    tagline: "Négoce physique de matières premières exécuté exclusivement via des entités corporate enregistrées, des transactions banque-à-banque certifiées et des financiers licenciés.",
    admin_link: "Registre de Conformité →",
  },
  lang: { switch_aria: "Changer de langue" },
};

const pt = {
  nav: {
    home: "Início",
    products: "Produtos",
    logistics: "Rede Logística",
    compliance: "Fiduciário & Conformidade",
    contact: "Contacto",
    cta: "Iniciar Negociação",
  },
  hero: {
    eyebrow: "Fundada 2026 · Mesa de Trading Pan-Africana",
    h1a: "Energia Soberana.",
    h1b: "Escala Institucional.",
    h1c: "Distribuição Pan-Africana.",
    sub: "A Vivasvan Energies Ventures (Pty) Ltd é uma trader e parceira logística de referência em commodities físicas, executando fluxos de energia e materiais de alto volume em todo o continente africano com precisão fiduciária absoluta.",
    cta_primary: "Consultar Catálogo",
    cta_secondary: "Portal de Conformidade & Licenças",
    trust: [
      { k: "AML/KYC", v: "Tolerância Zero" },
      { k: "LBMA", v: "Canais Certificados" },
      { k: "Banco-a-Banco", v: "Financiamento" },
      { k: "Registada", v: "Rede de Depósitos" },
    ],
  },
  products: {
    eyebrow: "Catálogo · 02",
    h2a: "Commodities físicas.",
    h2b: "Cadeias de fornecimento arquitetadas.",
    sub: "Duas verticais. Uma mesa de trading de nível institucional. Cada alocação é sustentada por contratos de refinaria verificados, armazenamento licenciado e instrumentos de off-take avalizados por bancos.",
    tab_energy: "Soluções de Energia & Gás",
    tab_solid: "Commodities Sólidas & Minerais",
    active: "Alocação Ativa",
    energy: [
      { name: "Diesel (10ppm / 50ppm)", body: "Gasóleo industrial de alta qualidade proveniente de refinarias de referência para frotas de transporte a granel, maquinaria pesada e redes industriais.", tag: "Granel / Industrial" },
      { name: "Gasolina (ULP 95 / 93)", body: "Combustível sem chumbo de queima limpa, cumprindo os parâmetros regulatórios SANS e regionais sul-africanos.", tag: "Grau Regulamentado" },
      { name: "Gás de Petróleo Liquefeito (GPL)", body: "Soluções térmicas a granel de alto poder calorífico, escala industrial, para fornos metalúrgicos e distribuição comercial pesada.", tag: "Alto Poder Calorífico" },
      { name: "Gás Natural Comprimido (GNC)", body: "Fórmulas de combustível gasoso alternativo altamente sustentáveis, otimizadas para a transição de frotas comerciais pesadas.", tag: "Transição de Frota" },
    ],
    solid: [
      { name: "Ouro (Au Bullion)", body: "Pipelines de elite para o comércio de ouro físico, operando estritamente através de canais seguros aprovados pela London Bullion Market Association (LBMA).", tag: "Canais LBMA" },
      { name: "Bauxite", body: "Minério de alumínio bruto de alta qualidade extraído diretamente de depósitos africanos prioritários para os principais corredores globais de exportação.", tag: "Corredor de Exportação" },
      { name: "Carvão", body: "Carvão térmico de alto PCI calibrado, projetado para redes elétricas, fornos industriais e metalurgia.", tag: "Térmico Alto-PCI" },
    ],
  },
  logistics: {
    eyebrow: "Poder Logístico · 03",
    h2a: "Uma rede construída sobre",
    h2b: "infraestrutura estratégica.",
    sub: "As nossas operações não dependem de especulação. Movimentamos produtos físicos com segurança através de acordos de off-take ativos e de relações de trabalho chave com os principais depósitos de armazenamento nos corredores logísticos críticos de África.",
    node: "Nó",
    active: "Ativo",
    depots: [
      { region: "África do Sul", node: "Distribuição Interior", body: "Reservas estratégicas e pipelines de distribuição interior que servem os setores industrial pesado e agrícola." },
      { region: "Namíbia", node: "Walvis Bay", body: "Integração portuária que impulsiona as cadeias de fornecimento de combustível e bauxite para os países vizinhos sem litoral da África Austral." },
      { region: "Moçambique", node: "Beira · Maputo", body: "Pipelines de off-take acelerados e libertações seguras de carga em terminais de águas profundas." },
      { region: "Tanzânia", node: "Dar es Salaam", body: "Integração direta de importação marítima ao serviço dos pipelines minerais corporativos de alta procura da África Oriental." },
      { region: "Botswana", node: "Corredor Interior", body: "Entregas energéticas consistentes e de alto volume que sustentam as infraestruturas mineiras de diamantes de uso intensivo." },
      { region: "RDC", node: "Cinturão de Cobre · Cobalto", body: "Soluções seguras de transporte de combustível a granel projetadas especificamente para minas de cobre e cobalto." },
    ],
  },
  compliance: {
    eyebrow: "Manifesto de Conformidade · 04",
    h2a: "Conformidade Fiduciária &",
    h2b: "Execução Licenciada.",
    body: "A Vivasvan Energies Ventures (Pty) Ltd opera sob um mandato de conformidade rigoroso e inflexível. Cada transação física, estrutura de trade finance e movimento logístico é executado exclusivamente através de entidades corporativas registadas, transações banco-a-banco certificadas e financiadores licenciados. Mantemos protocolos AML/KYC de tolerância zero e negociamos apenas com proprietários de depósitos, operadores portuários e instituições financeiras verificados, licenciados e acreditados. A segurança, legitimidade e o alinhamento regulatório são as nossas prioridades máximas.",
    pillars: [
      { k: "AML / KYC", v: "Protocolos de Tolerância Zero" },
      { k: "Banco-a-Banco", v: "Trade Finance Certificado" },
      { k: "Licenciados", v: "Depósitos & Portos" },
      { k: "Registadas", v: "Apenas Entidades Corporativas" },
    ],
  },
  contact: {
    eyebrow: "Conduta de Negociação · 05",
    h2a: "Estabelecer uma",
    h2b: "Conduta de Negociação.",
    sub: "Ligue-se à nossa mesa global de trading para negociar alocações, logística ou estruturas de financiamento de projetos. Todos os campos devem ser verificados para acionar o despacho de conformidade.",
    entity_label: "Entidade Registada",
    entity_value: "Vivasvan Energies Ventures (Pty) Ltd",
    portal_label: "Portal Oficial",
    desk_label: "Mesa de Trading",
    field_company: "Nome Legal da Empresa",
    field_license: "Licença Import-Export / Nº de Registo Corporativo",
    field_email: "Email Direto de Procurement",
    field_product: "Produto de Interesse",
    field_volume: "Volume Mensal Solicitado / Capacidade Requerida",
    placeholder_company: "ex. Sovereign Holdings Ltd",
    placeholder_license: "ex. IEC-2026-00891",
    placeholder_email: "procurement@suaempresa.com",
    placeholder_product: "Selecionar alocação…",
    placeholder_volume: "ex. 50 000 MT / mês",
    submit: "Submeter Pedido",
    submit_pending: "A despachar…",
    disclaimer: "As submissões são registadas no nosso livro de conformidade seguro. Entidades não verificáveis serão filtradas antes do despacho.",
    err_title: "Todos os campos devem ser verificados",
    err_desc: "Preencha cada campo para acionar o despacho de conformidade.",
    ok_title: "Pedido de conduta recebido",
    ok_desc: "A nossa mesa responderá através de um canal corporativo verificado.",
    fail_title: "Falha na submissão",
    fail_desc: "Por favor verifique todos os campos.",
  },
  footer: {
    copyright: "© 2026 Vivasvan Energies Ventures (Pty) Ltd · Fundada 2026 · Todos os Direitos Reservados",
    tagline: "Comércio físico de commodities executado exclusivamente via entidades corporativas registadas, transações banco-a-banco certificadas e financiadores licenciados.",
    admin_link: "Livro de Conformidade →",
  },
  lang: { switch_aria: "Mudar idioma" },
};

export const STRINGS = { en, fr, pt };
